import React, { useState, useCallback } from 'react';
import { useModal } from './ModalSystem';
import { TEABarChart, LCABarChart, ScenarioCompareChart } from './ResultsCharts';
import DesignParametersPanel from './DesignParametersPanel';
import { getDesignParameters } from '../utils/designParameters';
import AutoLinkPanel from './AutoLinkPanel';
import SourcesButton from './SourcesButton';
import { isTemplateMode } from '../services/lcaCalculator';
import { computeFUNormalizer, defaultEnergyDensity } from '../services/functionalUnit';
import { formatCitedShare } from '../services/costProvenance';
import {
  ECOINVENT_SOURCE, GREET_SOURCE, IMPACT_SOURCE_ORDER, getActiveImpactSource, setActiveImpactSource,
  impactSourceLabel, impactSourceCoverage, formatCoverageShare, isCategoryCovered,
  IMPACT_SOURCE_CITATIONS, TRACI_CITATION,
} from '../services/impactSource';

/**
 * ResultsPanel — Template Mode
 *
 * Displays LCA and TEA results computed by lcaCalculator.js.
 * Two tabs: LCA (TRACI v2.1 impacts) and TEA (material costs).
 *
 * Props:
 *   project          – current project object
 *   analysisResults  – { lca, tea } from lcaCalculator
 *   isAnalyzing      – boolean spinner flag
 *   onRunLCA         – callback to trigger LCA
 *   onRunTEA         – callback to trigger TEA
 *   onExport         – callback to export results
 *   onUpdateProject  – callback to update project (e.g. scaling factor)
 */
const ResultsPanel = ({
  project,
  analysisResults,
  isAnalyzing,
  isDemoBuild,
  onRunLCA,
  onRunTEA,
  onRunCircularity,
  onExport,
  onUpdateProject,
}) => {
  const { showAlert } = useModal();
  const [activeTab, setActiveTab] = useState('lca');
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [expandedProcess, setExpandedProcess] = useState(null);
  const [expandedMaterial, setExpandedMaterial] = useState(null);
  const [scenarios, setScenarios] = useState([]);        // [{ name, timestamp, lca, tea }]
  const [scenarioName, setScenarioName] = useState('');
  const [showSaveScenario, setShowSaveScenario] = useState(false);
  const [compareA, setCompareA] = useState(0);
  const [compareB, setCompareB] = useState(1);

  // -- Formatting helpers -----------------------------------------------------

  const fmt = (n, decimals = 4) => {
    if (n === undefined || n === null || typeof n !== 'number') return '—';
    if (n === 0) return '0';
    if (Math.abs(n) < 0.0001 && n !== 0) return n.toExponential(2);
    if (Math.abs(n) >= 10000) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
    return Number(n.toFixed(decimals)).toString();
  };

  const fmtCurrency = (n) => {
    if (typeof n !== 'number') return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(n);
  };

  // -- Impact dataset -----------------------------------------------------------

  const activeSource = getActiveImpactSource(project);

  // Switching recomputes straight away when results are on screen, so a number
  // from one dataset is never shown under the other's label.
  const switchDataset = (source) => {
    if (!onUpdateProject || !project || source === activeSource) return;
    onUpdateProject(setActiveImpactSource(project, source));
    if (analysisResults?.lca && onRunLCA) Promise.resolve().then(onRunLCA);
  };

  // Apply changes quantities and Wh/kg, which every analysis reads. Re-run the
  // ones on screen so they never describe the previous design.
  const applyDesign = async (updated) => {
    if (!onUpdateProject) return;
    await onUpdateProject(updated);
    const { lca, tea, circularity } = analysisResults || {};
    if (lca && onRunLCA) await onRunLCA();
    if (tea && onRunTEA) await onRunTEA();
    if (circularity && onRunCircularity) await onRunCircularity();
  };

  // What the scores rest on: the dataset and method citations, and the inputs
  // the dataset cannot score (they count as zero). Categories it cannot score
  // at all read "not covered" in the table.
  const scoreSources = (source) => {
    const cov = impactSourceCoverage(project, source);
    const missing = [...new Set(cov.unlinked)];
    const share = cov.unlinkedCarbonShare;
    const notes = missing.length === 0 ? [] : [
      `Not scored, so counted as zero: ${missing.join(', ')}`
        + (share !== null ? ` (about ${formatCoverageShare(share)} of the carbon by ${impactSourceLabel(cov.estimatedWith)}).` : '.'),
    ];
    return {
      cov,
      missing,
      sources: [IMPACT_SOURCE_CITATIONS[source], TRACI_CITATION].filter(Boolean),
      notes,
    };
  };

  // One quiet line when the active dataset leaves inputs unscored.
  const renderCoverageNote = () => {
    if (activeSource === ECOINVENT_SOURCE) return null;
    const { cov, missing, sources, notes } = scoreSources(activeSource);
    if (missing.length === 0) return null;
    return (
      <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#6b7a8a' }}>
        <span>{cov.linked} of {cov.total} inputs scored</span>
        <SourcesButton title="Impact data" sources={sources} notes={notes} />
      </div>
    );
  };

  // -- Configuration section --------------------------------------------------

  const renderConfig = () => {
    const scalingFactor = project?.project_info?.scaling_factor || 1.0;
    const lifeExtension = project?.project_info?.battery_life_extension || 1.0;
    const chemistry = project?.project_info?.chemistry || '—';
    const category = project?.project_info?.category || '—';
    const processCount = project?.processes ? Object.keys(project.processes).length : 0;
    const projectName = project?.project_info?.name || project?.project_name || '—';
    const fu = project?.functional_unit || {};
    // Build a short functional unit label
    const fuShort = fu.scale === 'module'
      ? `Used battery module (${fu.weight_kg || '—'} kg)`
      : fu.description || `1 ${fu.scale || 'unit'} of ${chemistry} cell`;

    // -- Functional unit basis (per kWh vs per kg) ----------------------------
    // Default to 'energy' to match computeFUNormalizer (per kWh is the standard
    // battery basis); the Energy Density field below shows the conversion factor.
    const fuBasis = project?.project_info?.fu_basis || 'energy';
    const energyDensity = project?.project_info?.energy_density_wh_per_kg ?? fu.energy_density_wh_per_kg ?? defaultEnergyDensity(project) ?? '';
    const norm = computeFUNormalizer(project);
    const fuDetail = norm.perKwh
      ? `${fuShort} → reported per kWh (this unit stores ${norm.kwh.toFixed(3)} kWh)`
      : `${fuShort} → reported per kg`;
    const fuTooltip = norm.perKwh
      ? `The physical unit is ${fuShort.toLowerCase()}. At ${norm.whPerKg} Wh/kg it stores ${norm.kwh.toFixed(3)} kWh, and every impact and cost is divided by that energy — so you compare chemistries per kWh of capacity, not per kg of material.`
      : 'Results are shown per kg of product. Switch Basis to "Per kWh" to normalize by the energy the cell stores.';

    // Local updater for project_info fields (mirrors the scaling-factor pattern)
    const updateInfo = (patch) => {
      if (onUpdateProject && project) {
        onUpdateProject({ ...project, project_info: { ...project.project_info, ...patch } });
      }
    };

    // Only show Life Extension for modified chemistry templates (not coating, not recycling)
    const showLifeExtension = category.toLowerCase().includes('modified') && !category.toLowerCase().includes('coating');

    return (
      <div className="configuration-section">
        <h4>Configuration</h4>
        <div className="config-grid">
          <div className="config-item">
            <label>Process:</label>
            <span className="config-value-plain" style={{ fontSize: '12px' }}>
              {projectName} ({chemistry}, {processCount} steps)
            </span>
          </div>
          <div className="config-item">
            <label>Functional Unit:</label>
            <span className="config-value-plain" style={{ fontSize: '12px', cursor: 'help' }} title={fuTooltip}>{fuDetail}</span>
          </div>
          <div className="config-item">
            <label>Basis:</label>
            <select
              value={fuBasis}
              onChange={(e) => updateInfo({ fu_basis: e.target.value })}
              className="config-input"
              title="Functional unit basis. Per kWh normalizes impacts/cost by the cell's energy content — the metric used for cross-chemistry comparison."
            >
              <option value="energy">Per kWh</option>
              <option value="mass">Per kg</option>
            </select>
          </div>
          {fuBasis === 'energy' && (
            <div className="config-item">
              <label>Energy density:</label>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <input
                  type="number"
                  value={energyDensity}
                  onChange={(e) => {
                    const v = e.target.value;
                    updateInfo({ energy_density_wh_per_kg: v === '' ? null : (parseFloat(v) || null) });
                  }}
                  className="config-input"
                  min="1"
                  step="10"
                  placeholder="Wh/kg"
                  style={{ width: '70px' }}
                />
                <span style={{ fontSize: '11px', color: '#666' }}>Wh/kg</span>
              </span>
            </div>
          )}
          {norm.fallback && (
            <div style={{ gridColumn: '1 / -1', fontSize: '11px', color: '#b26a00', background: '#fff8e1', border: '1px solid #ffe0a3', borderRadius: '4px', padding: '4px 8px' }}>
              ⚠ No energy density set — showing results <strong>per kg</strong>. Enter a value above to normalize per kWh.
            </div>
          )}
          <div className="config-item">
            <label>Quantity:</label>
            <input
              type="number"
              value={scalingFactor}
              onChange={(e) => {
                const val = parseFloat(e.target.value) || 1.0;
                if (onUpdateProject && project) {
                  onUpdateProject({
                    ...project,
                    project_info: { ...project.project_info, scaling_factor: val },
                  });
                }
              }}
              className="config-input"
              min="0.01"
              step="0.1"
            />
          </div>
          {showLifeExtension && (
            <div className="config-item">
              <label>Life Extension:</label>
              <input
                type="number"
                value={lifeExtension}
                onChange={(e) => {
                  const val = parseFloat(e.target.value) || 1.0;
                  if (onUpdateProject && project) {
                    onUpdateProject({
                      ...project,
                      project_info: { ...project.project_info, battery_life_extension: Math.max(0.1, val) },
                    });
                  }
                }}
                className="config-input"
                min="0.1"
                step="0.1"
                title="Battery life extension factor from modified chemistry. Results are divided by this to show impact per unit of service life."
              />
            </div>
          )}
          {activeTab === 'lca' && (
            <div className="config-item" data-coach="impact-source">
              <label>Impact data:</label>
              <select
                value={activeSource}
                onChange={(e) => switchDataset(e.target.value)}
                className="config-input"
                title="Which dataset supplies the TRACI v2.1 scores. GREET 2025 is open data used exactly; inputs without a GREET equivalent stay unlinked rather than borrowing ecoinvent values. ecoinvent template inputs are exact, but inputs added from the ecoinvent lookup carry illustrative (noised) values."
              >
                {IMPACT_SOURCE_ORDER.map((s) => (
                  <option key={s} value={s}>{impactSourceLabel(s)}{s === GREET_SOURCE ? ' (open data)' : ''}</option>
                ))}
              </select>
            </div>
          )}
          {activeTab === 'lca' && (
            <div className="config-item">
              <label>Method:</label>
              <span
                className="config-value-plain"
                title="TRACI v2.1 characterization. Each input's scores are computed offline from the selected dataset and shipped with the app; the LCA sums them over the inventory."
              >TRACI v2.1</span>
            </div>
          )}
          {activeTab === 'lca' && renderCoverageNote()}
          {lifeExtension > 1.0 && (
            <div style={{ gridColumn: '1 / -1', fontSize: '11px', color: '#2e7d32', padding: '4px 0', fontStyle: 'italic' }}>
              Life extension {lifeExtension}× applied — results reflect impact per unit of extended service life
            </div>
          )}
        </div>
      </div>
    );
  };

  // -- LCA results ------------------------------------------------------------

  const renderLCAResults = () => {
    const lca = analysisResults?.lca;
    if (!lca || !lca.results) return null;

    if (lca.error) {
      return <div className="no-results"><p style={{ color: '#dc3545' }}>Error: {lca.error}</p></div>;
    }

    const results = lca.results; // keyed by "TRACI v2.1 - Label"
    const categories = Object.entries(results);
    // Results saved before datasets existed carry no impact_source: ecoinvent.
    const resultSource = lca.impact_source || ECOINVENT_SOURCE;
    const stale = resultSource !== activeSource;
    const resultSources = scoreSources(resultSource);

    return (
      <div className="results-content" style={{ padding: '12px' }}>
        {stale && (
          <div style={{ fontSize: '12px', color: '#7a4d00', background: '#fff8e1', border: '1px solid #ffe0a3', borderRadius: '4px', padding: '6px 10px', marginBottom: '8px' }}>
            These results use {impactSourceLabel(resultSource)}. Run LCA to recompute them with {impactSourceLabel(activeSource)}.
          </div>
        )}
        <div className="lca-grid">
        {/* Summary table */}
        <div className="result-section">
          <h4 style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            Environmental Impacts — TRACI v2.1 · {impactSourceLabel(resultSource)}
            <SourcesButton title="Impact data" sources={resultSources.sources} notes={resultSources.notes} />
          </h4>
          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #dee2e6', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px' }}>Impact Category</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Total</th>
                <th style={{ padding: '6px 8px' }}>Unit</th>
              </tr>
            </thead>
            <tbody>
              {categories.map(([key, data]) => {
                const label = key.replace('TRACI v2.1 - ', '');
                const isExpanded = expandedCategory === key;

                return (
                  <React.Fragment key={key}>
                    <tr
                      style={{
                        borderBottom: '1px solid #f0f0f0',
                        cursor: 'pointer',
                        background: isExpanded ? '#f0f7ff' : 'transparent',
                      }}
                      onClick={() => setExpandedCategory(isExpanded ? null : key)}
                      title="Click to expand process breakdown"
                    >
                      <td style={{ padding: '6px 8px' }}>
                        <span style={{ marginRight: '6px', fontSize: '10px', color: '#999' }}>
                          {isExpanded ? '▼' : '▶'}
                        </span>
                        {label}
                      </td>
                      {isCategoryCovered(resultSource, data.category_key) ? (
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                          {fmt(data.total)}
                        </td>
                      ) : (
                        <td
                          style={{ padding: '6px 8px', textAlign: 'right', color: '#999', fontStyle: 'italic', fontSize: '12px' }}
                          title={`${impactSourceLabel(resultSource)} has no emissions that count toward this category, so it cannot be scored. This is not a zero impact.`}
                        >
                          not covered
                        </td>
                      )}
                      <td style={{ padding: '6px 8px', color: '#666', fontSize: '12px' }}>{data.unit}</td>
                    </tr>

                    {/* Process breakdown */}
                    {isExpanded && data.process_breakdown && isCategoryCovered(resultSource, data.category_key) && (
                      Object.entries(data.process_breakdown).map(([procId, pb]) => (
                        <tr key={`${key}-${procId}`} style={{ background: '#f8f9fa', fontSize: '12px' }}>
                          <td style={{ padding: '4px 8px 4px 28px', color: '#555' }}>
                            {(pb.process_name || procId).replace(/_/g, ' ')}
                            <span style={{ color: '#999', marginLeft: '6px' }}>
                              ({(pb.inputs || []).length} inputs)
                            </span>
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'monospace' }}>
                            {fmt(pb.total)}
                          </td>
                          <td style={{ padding: '4px 8px', color: '#999', fontSize: '11px' }}>{data.unit}</td>
                        </tr>
                      ))
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* LCA Chart */}
        <div className="result-section lca-chart-cell">
          <LCABarChart analysisResults={analysisResults} selectedCategory={expandedCategory} />
        </div>
        </div>{/* /lca-grid */}

        {/* Scaling info */}
        {lca.scaling_factor && lca.scaling_factor !== 1 && (
          <div style={{ fontSize: '12px', color: '#666', marginTop: '8px', fontStyle: 'italic' }}>
            Results scaled by factor: {lca.scaling_factor}
          </div>
        )}

      </div>
    );
  };

  // -- TEA results ------------------------------------------------------------

  // A dollar figure with no stated vintage is not defensible to anyone outside
  // the room. One line says how much of the total rests on a citation; the
  // citations, and what is left uncited, sit behind its (i).
  const renderCostProvenance = (prov, note) => {
    const share = formatCitedShare(prov.cited_share);
    const span = prov.year_min
      ? (prov.year_min === prov.year_max ? `${prov.year_min}` : `${prov.year_min}\u2013${prov.year_max}`)
      : null;
    const attributable = prov.cited_cost + prov.uncited_cost;
    const sources = prov.sources.map(s => ({
      cite: s.source,
      used_for: attributable > 0 ? `${formatCitedShare(s.cost / attributable)} of cost` : '',
    }));
    const notes = [
      prov.uncited_inputs > 0 && `${prov.uncited_inputs} input${prov.uncited_inputs === 1 ? '' : 's'} priced without a citation`
        + (prov.unverified_inputs > 0 ? `, ${prov.unverified_inputs} naming a source that does not carry that price.` : '.'),
      prov.linked_inputs > 0 && `${fmtCurrency(prov.linked_cost)} comes from linked templates, cited there.`,
      note,
    ].filter(Boolean);
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '6px', margin: '0 0 10px', fontSize: '12px',
        color: prov.cited_share >= 0.5 ? '#6b7a8a' : '#8d6e00',
      }}>
        <span>{share} of cost from cited prices{span ? `, ${span}` : ''}</span>
        <SourcesButton title="Cost sources" sources={sources} notes={notes} />
      </div>
    );
  };

  const renderTEAResults = () => {
    const tea = analysisResults?.tea;

    if (!tea || !tea.success) {
      return null;
    }

    if (tea.error) {
      return <div className="no-results"><p style={{ color: '#dc3545' }}>Error: {tea.error}</p></div>;
    }

    const breakdown = tea.process_breakdown || {};
    const processes = Object.entries(breakdown);
    // Per-kWh basis adds a "/ kWh" suffix to monetary values.
    const costSuffix = tea.functional_unit_basis === 'energy' ? ' / kWh' : '';
    const suffixEl = costSuffix
      ? <span style={{ fontSize: '12px', color: '#666', fontWeight: 400 }}>{costSuffix}</span>
      : null;

    return (
      <div className="results-content" style={{ padding: '12px' }}>
        {/* Total cost */}
        <div className="result-section">
          <h4>Cost Summary</h4>
          <div className="result-item" style={{ background: '#f0f7ff', padding: '10px', borderRadius: '6px', marginBottom: '8px' }}>
            <span className="result-label" style={{ fontWeight: 600, fontSize: '14px' }}>Total Material/Energy Cost</span>
            <span className="result-value" style={{ fontWeight: 700, fontSize: '16px', color: '#1565c0' }}>
              {fmtCurrency(tea.total_cost)}{suffixEl}
            </span>
          </div>

          {/* Revenue & profit — only when at least one output has price_per_unit set */}
          {tea.has_revenue && (
            <>
              <div className="result-item" style={{ background: '#e8f5e9', padding: '10px', borderRadius: '6px', marginBottom: '8px' }}>
                <span className="result-label" style={{ fontWeight: 600, fontSize: '14px' }}>Total Revenue</span>
                <span className="result-value" style={{ fontWeight: 700, fontSize: '16px', color: '#2e7d32' }}>
                  {fmtCurrency(tea.total_revenue)}{suffixEl}
                </span>
              </div>
              <div
                className="result-item"
                style={{
                  background: tea.total_profit >= 0 ? '#f1f8e9' : '#ffebee',
                  padding: '10px', borderRadius: '6px', marginBottom: '8px',
                  border: `1px solid ${tea.total_profit >= 0 ? '#aed581' : '#ef9a9a'}`,
                }}
              >
                <span className="result-label" style={{ fontWeight: 600, fontSize: '14px' }}>
                  Profit {tea.total_revenue > 0 ? `(margin ${((tea.total_profit / tea.total_revenue) * 100).toFixed(1)}%)` : ''}
                </span>
                <span className="result-value" style={{
                  fontWeight: 700, fontSize: '16px',
                  color: tea.total_profit >= 0 ? '#2e7d32' : '#c62828',
                }}>
                  {fmtCurrency(tea.total_profit)}
                </span>
              </div>
            </>
          )}

          {tea.cost_provenance && renderCostProvenance(tea.cost_provenance, tea.note)}
        </div>

        {/* Process breakdown */}
        <div className="result-section">
          <h4>Cost by Process</h4>
          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #dee2e6', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px' }}>Process</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Cost</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Items</th>
              </tr>
            </thead>
            <tbody>
              {processes.map(([procId, pd]) => {
                const isExpanded = expandedProcess === procId;
                return (
                  <React.Fragment key={procId}>
                    <tr
                      style={{
                        borderBottom: '1px solid #f0f0f0',
                        cursor: 'pointer',
                        background: isExpanded ? '#f0f7ff' : 'transparent',
                      }}
                      onClick={() => setExpandedProcess(isExpanded ? null : procId)}
                    >
                      <td style={{ padding: '6px 8px' }}>
                        <span style={{ marginRight: '6px', fontSize: '10px', color: '#999' }}>
                          {isExpanded ? '▼' : '▶'}
                        </span>
                        {(pd.process_name || procId).replace(/_/g, ' ')}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                        {fmtCurrency(pd.total_cost)}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: '#666' }}>
                        {(pd.items || []).length}
                      </td>
                    </tr>

                    {/* Item detail */}
                    {isExpanded && (pd.items || []).map((item, idx) => (
                      <tr key={`${procId}-${idx}`} style={{ background: '#f8f9fa', fontSize: '12px' }}>
                        <td style={{ padding: '4px 8px 4px 28px', color: item.internally_produced ? '#aaa' : '#555' }}>
                          {item.name}
                          {item.internally_produced ? (
                            <span style={{ color: '#bbb', marginLeft: '6px', fontStyle: 'italic' }}>
                              ({fmt(item.quantity, 4)} {item.unit} — cost in upstream process)
                            </span>
                          ) : (
                            <span style={{ color: '#999', marginLeft: '6px' }}>
                              ({fmt(item.quantity, 4)} {item.unit} × {fmtCurrency(item.cost_per_unit)})
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'monospace', color: item.internally_produced ? '#bbb' : 'inherit' }}>
                          {item.internally_produced ? '—' : fmtCurrency(item.total_cost)}
                        </td>
                        <td />
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}

              {/* Total row */}
              <tr style={{ borderTop: '2px solid #dee2e6', fontWeight: 700, background: '#f8f9fa' }}>
                <td style={{ padding: '8px' }}>Total</td>
                <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace', color: '#1565c0' }}>
                  {fmtCurrency(tea.total_cost)}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>

        {tea.scaling_factor && tea.scaling_factor !== 1 && (
          <div style={{ fontSize: '12px', color: '#666', marginTop: '8px', fontStyle: 'italic' }}>
            Results scaled by factor: {tea.scaling_factor}
          </div>
        )}

        {/* TEA Chart */}
        <div className="result-section">
          <TEABarChart analysisResults={analysisResults} />
        </div>

      </div>
    );
  };

  // -- Scenario comparison -----------------------------------------------------

  const handleSaveScenario = useCallback(() => {
    if (!scenarioName.trim()) return;
    const scenario = {
      name: scenarioName.trim(),
      timestamp: new Date().toISOString(),
      lca: analysisResults?.lca || null,
      tea: analysisResults?.tea || null,
    };
    setScenarios(prev => [...prev, scenario]);
    setScenarioName('');
    setShowSaveScenario(false);
    showAlert(`Scenario "${scenario.name}" saved.`, { title: 'Scenario Saved', type: 'info' });
  }, [scenarioName, analysisResults, showAlert]);

  const renderScenarioCompare = () => {
    if (scenarios.length < 2) {
      return (
        <div className="results-content" style={{ padding: '12px' }}>
          <div className="result-section">
            <h4>Scenario Comparison</h4>
            <p style={{ fontSize: '13px', color: '#666' }}>
              Save at least 2 scenarios to compare. Run LCA or TEA, then click "Save as Scenario" below.
            </p>
            <p style={{ fontSize: '12px', color: '#999' }}>
              {scenarios.length === 0 ? 'No scenarios saved yet.' : `1 scenario saved: "${scenarios[0].name}"`}
            </p>
          </div>
        </div>
      );
    }

    const sA = scenarios[compareA] || scenarios[0];
    const sB = scenarios[compareB] || scenarios[1];

    const fmtDelta = (a, b) => {
      if (typeof a !== 'number' || typeof b !== 'number') return '—';
      const d = b - a;
      const pct = a !== 0 ? ((d / Math.abs(a)) * 100).toFixed(1) : '—';
      const sign = d > 0 ? '+' : '';
      return `${sign}${d.toLocaleString(undefined, { maximumFractionDigits: 2 })} (${sign}${pct}%)`;
    };

    return (
      <div className="results-content" style={{ padding: '12px' }}>
        <div className="result-section">
          <h4>Scenario Comparison</h4>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px', fontSize: '12px' }}>
            <label>A:</label>
            <select value={compareA} onChange={e => setCompareA(Number(e.target.value))}
              style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', fontSize: '12px', flex: 1 }}>
              {scenarios.map((s, i) => <option key={i} value={i}>{s.name}</option>)}
            </select>
            <label>B:</label>
            <select value={compareB} onChange={e => setCompareB(Number(e.target.value))}
              style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', fontSize: '12px', flex: 1 }}>
              {scenarios.map((s, i) => <option key={i} value={i}>{s.name}</option>)}
            </select>
          </div>
        </div>

        {/* TEA comparison table */}
        {sA.tea?.success && sB.tea?.success && (
          <div className="result-section">
            <h4>TEA Comparison</h4>
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #dee2e6', textAlign: 'left' }}>
                  <th style={{ padding: '6px 8px' }}>Metric</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>{sA.name}</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>{sB.name}</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Delta</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #f0f0f0', fontWeight: 600 }}>
                  <td style={{ padding: '6px 8px' }}>Total Cost</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtCurrency(sA.tea.total_cost)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtCurrency(sB.tea.total_cost)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', color: sB.tea.total_cost > sA.tea.total_cost ? '#c62828' : '#2e7d32' }}>
                    {fmtDelta(sA.tea.total_cost, sB.tea.total_cost)}
                  </td>
                </tr>
              </tbody>
            </table>
            <ScenarioCompareChart scenarioA={sA} scenarioB={sB} mode="tea" />
          </div>
        )}

        {/* LCA comparison table */}
        {sA.lca?.results && sB.lca?.results && (
          <div className="result-section">
            <h4>LCA Comparison</h4>
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #dee2e6', textAlign: 'left' }}>
                  <th style={{ padding: '6px 8px' }}>Category</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>{sA.name}</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>{sB.name}</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Delta</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(sA.lca.results).map(cat => {
                  const dA = sA.lca.results[cat];
                  const dB = sB.lca.results[cat];
                  return (
                    <tr key={cat} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '5px 8px', fontSize: '11px' }}>{cat.replace('TRACI v2.1 - ', '')}</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(dA?.total)}</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(dB?.total)}</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: 'monospace', fontSize: '11px', color: (dB?.total || 0) > (dA?.total || 0) ? '#c62828' : '#2e7d32' }}>
                        {fmtDelta(dA?.total, dB?.total)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <ScenarioCompareChart scenarioA={sA} scenarioB={sB} mode="lca" />
          </div>
        )}
      </div>
    );
  };

  // -- Circularity results -----------------------------------------------------

  const renderCircularityResults = () => {
    const circ = analysisResults?.circularity;
    if (!circ || !circ.success) return null;

    const mciColor = (val) => {
      if (val >= 0.7) return '#28a745';
      if (val >= 0.4) return '#ffc107';
      if (val >= 0.1) return '#fd7e14';
      return '#dc3545';
    };

    return (
      <div className="results-content" style={{ padding: '12px' }}>
        {/* Overall MCI */}
        <div className="result-section">
          <h4>Overall Material Circularity Index</h4>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '16px',
            background: '#f0f7ff', padding: '14px 16px', borderRadius: '8px', marginBottom: '12px'
          }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%',
              border: `4px solid ${mciColor(circ.overall_mci)}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '18px', fontWeight: 700, color: mciColor(circ.overall_mci),
              flexShrink: 0,
            }}>
              {circ.overall_mci.toFixed(2)}
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#333', marginBottom: '2px' }}>
                {circ.overall_mci >= 0.7 ? 'High Circularity' :
                 circ.overall_mci >= 0.4 ? 'Moderate Circularity' :
                 circ.overall_mci >= 0.1 ? 'Low Circularity' : 'Linear (No Circularity)'}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {circ.is_recycling ? 'Recycling process' : 'Manufacturing process'} — {circ.materials.length} materials tracked
              </div>
            </div>
          </div>
        </div>

        {/* Per-material breakdown — click a row for the virgin/recycled/recovered detail */}
        <div className="result-section">
          <h4>Material Circularity Breakdown</h4>
          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #dee2e6', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px' }}>Material</th>
                <th style={{ padding: '6px 8px', textAlign: 'center', width: '60px' }}>MCI</th>
                <th style={{ padding: '6px 8px', width: '120px' }}></th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Recovered</th>
              </tr>
            </thead>
            <tbody>
              {circ.materials.map((mat) => {
                const isExpanded = expandedMaterial === mat.key;
                return (
                  <React.Fragment key={mat.key}>
                    <tr
                      style={{
                        borderBottom: '1px solid #f0f0f0', cursor: 'pointer',
                        background: isExpanded ? '#f0f7ff' : 'transparent',
                      }}
                      onClick={() => setExpandedMaterial(isExpanded ? null : mat.key)}
                      title="Click for material flow detail"
                    >
                      <td style={{ padding: '8px' }}>
                        <span style={{ marginRight: '6px', fontSize: '10px', color: '#999' }}>{isExpanded ? '▼' : '▶'}</span>
                        <span style={{ fontWeight: 500 }}>{mat.label}</span>
                        <span style={{ color: '#999', fontSize: '11px', marginLeft: '4px' }}>({mat.element})</span>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center', fontFamily: 'monospace', fontWeight: 600, color: mciColor(mat.mci) }}>
                        {mat.mci.toFixed(2)}
                      </td>
                      <td style={{ padding: '8px' }}>
                        <div style={{ width: '100%', height: '8px', background: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{
                            width: `${Math.max(mat.mci * 100, 1)}%`, height: '100%',
                            background: mciColor(mat.mci), borderRadius: '4px', transition: 'width 0.3s ease',
                          }} />
                        </div>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right', fontSize: '12px', color: '#555' }}>
                        {mat.recovered_output_kg > 0 ? `${mat.recovered_output_kg} kg` : '—'}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr style={{ background: '#f8f9fa' }}>
                        <td colSpan={4} style={{ padding: '8px 12px 10px 28px' }}>
                          <div className="circ-detail-grid">
                            <span>Virgin input <b>{mat.virgin_input_kg} kg</b></span>
                            <span>Recycled input <b>{mat.recycled_input_kg} kg</b></span>
                            <span>Recovered output <b>{mat.recovered_output_kg} kg</b></span>
                            <span>Recycled fraction <b>{(mat.recycled_input_fraction * 100).toFixed(1)}%</b></span>
                            <span>Recovery fraction <b>{(mat.recovery_fraction * 100).toFixed(1)}%</b></span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          <div style={{ fontSize: '11px', color: '#999', marginTop: '6px', fontStyle: 'italic' }}>
            Click a material for its virgin / recycled / recovered flows.
          </div>
        </div>

        {circ.note && (
          <div style={{ fontSize: '11px', color: '#888', fontStyle: 'italic', marginTop: '8px' }}>
            {circ.note}
          </div>
        )}
      </div>
    );
  };

  // -- Main render ------------------------------------------------------------

  const hasLCA = analysisResults?.lca?.results && Object.keys(analysisResults.lca.results).length > 0;
  const hasTEA = analysisResults?.tea?.success;
  const hasCirc = analysisResults?.circularity?.success;

  return (
    <div className="panel results-panel">
      {/* Tabs */}
      <div className="results-tabs">
        <button className={`tab-button ${activeTab === 'lca' ? 'active' : ''}`} onClick={() => setActiveTab('lca')}>
          Life Cycle
        </button>
        <button className={`tab-button ${activeTab === 'tea' ? 'active' : ''}`} onClick={() => setActiveTab('tea')}>
          Techno-Economic
        </button>
        <button className={`tab-button ${activeTab === 'circularity' ? 'active' : ''}`} onClick={() => setActiveTab('circularity')}>
          Circularity<sup style={{ fontSize: '9px', marginLeft: '2px', fontWeight: 400 }}>beta</sup>
        </button>
      </div>

      <div className="panel-content" style={{ overflow: 'auto', flex: 1, position: 'relative' }}>
        {/* Design parameters panel (only for templates that declare them) - on every tab. It
            scrolls with the results rather than in a box of its own, so none of it is cut off. */}
        {getDesignParameters(project) && (
          <div className="results-inner">
            <DesignParametersPanel
              project={project}
              onUpdateProject={applyDesign}
            />
          </div>
        )}

        <div className="results-inner">
        {project && renderConfig()}

        {/* Auto-link panel — shown in LCA tab when no TRACI scores baked in */}
        {activeTab === 'lca' && project && !isTemplateMode(project) && (
          <AutoLinkPanel project={project} onProjectUpdated={onUpdateProject} />
        )}

        {/* Web-only note: synthetic ecoinvent data — kept subtle, routes to the feedback form */}
        {activeTab === 'lca' && isDemoBuild && (
          <div className="lca-demo-note">
            <span>Preview values are illustrative.</span>
            <a
              href="https://litecab.github.io/#contact"
              target="_blank"
              rel="noopener noreferrer"
            >Request a full LCA →</a>
          </div>
        )}

        {/* Run buttons — centered below config */}
        <div className="results-run-bar">
          {activeTab === 'lca' && (
            <>
              {!hasLCA && !isAnalyzing && isTemplateMode(project) && (
                <p style={{ fontSize: '13px', color: '#666', margin: '0 0 8px', width: '100%', textAlign: 'center' }}>No LCA results yet.</p>
              )}
              <button className="btn btn-run" onClick={onRunLCA} disabled={isAnalyzing || !isTemplateMode(project)}>
                {isAnalyzing ? 'Running…' : hasLCA ? 'Re-run LCA' : 'Run LCA Analysis'}
              </button>
            </>
          )}
          {activeTab === 'tea' && (
            <>
              {!hasTEA && !isAnalyzing && <p style={{ fontSize: '13px', color: '#666', margin: '0 0 8px', width: '100%', textAlign: 'center' }}>No TEA results yet.</p>}
              <button className="btn btn-run" onClick={onRunTEA} disabled={isAnalyzing}>
                {isAnalyzing ? 'Running…' : hasTEA ? 'Re-run TEA' : 'Run TEA Analysis'}
              </button>
            </>
          )}
          {activeTab === 'circularity' && (
            <>
              {!hasCirc && !isAnalyzing && <p style={{ fontSize: '13px', color: '#666', margin: '0 0 8px', width: '100%', textAlign: 'center' }}>No circularity results yet.</p>}
              <button className="btn btn-run" onClick={onRunCircularity} disabled={isAnalyzing}>
                {isAnalyzing ? 'Running…' : hasCirc ? 'Re-run Circularity' : 'Run Circularity Analysis'}
              </button>
            </>
          )}
        </div>

        {/* Spinner */}
        {isAnalyzing && (
          <div className="loading" style={{ padding: '20px', textAlign: 'center' }}>
            <div className="spinner" />
            Running analysis…
          </div>
        )}

        {/* Results */}
        {!isAnalyzing && (
          <div className="tab-content">
            {activeTab === 'lca' && renderLCAResults()}
            {activeTab === 'tea' && renderTEAResults()}
            {activeTab === 'circularity' && renderCircularityResults()}
          </div>
        )}
        </div>{/* /results-inner */}

        {/* Save Scenario + Export buttons */}
        <div className="results-export-bar" style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {(hasLCA || hasTEA) && (
            <>
              {showSaveScenario ? (
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Scenario name..."
                    value={scenarioName}
                    onChange={e => setScenarioName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSaveScenario()}
                    autoFocus
                    style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: '12px', width: '140px' }}
                  />
                  <button className="btn btn-run" onClick={handleSaveScenario} disabled={!scenarioName.trim()}
                    style={{ padding: '4px 10px', fontSize: '12px' }}>Save</button>
                  <button className="btn" onClick={() => setShowSaveScenario(false)}
                    style={{ padding: '4px 10px', fontSize: '12px' }}>Cancel</button>
                </div>
              ) : (
                <button className="btn" onClick={() => { setShowSaveScenario(true); setScenarioName(project?.project_info?.name || 'Scenario'); }}
                  style={{ padding: '5px 12px', fontSize: '12px', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 500 }}>
                  Save as Scenario
                </button>
              )}
            </>
          )}
          {((activeTab === 'lca' && hasLCA) || (activeTab === 'tea' && hasTEA) || (activeTab === 'circularity' && hasCirc)) && (
            <button className="btn btn-export" onClick={onExport}>
              Export Results
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResultsPanel;
